import { Action, Module, Mutation, VuexModule } from 'vuex-module-decorators';
import { OrderDto, PagedOrdersDto, PageMetadataDto, PageRequestDto } from '@/api';
import { cloneDeep } from 'lodash';
import { SearchUtility } from '@/stores/search/util';
import { StoreResponse, StoreResponses } from '@/stores/response';
import { ordersApi } from '@/stores/api';
import ErrorKey from '@/util/ErrorKey';

@Module({
  namespaced: true,
  name: 'OrderHistoryStore',
})
export default class OrderHistoryStore extends VuexModule {
  private orders: OrderDto[] = [];
  private pageRequest: PageRequestDto = cloneDeep(SearchUtility.EmptyPageRequest);
  private pageMetadata: PageMetadataDto = cloneDeep(SearchUtility.EmptyPageResult);
  private selectedOrder: OrderDto | null = null;

  public get currentOrders(): OrderDto[] {
    return this.orders;
  }

  public get currentPageRequest(): PageRequestDto {
    return this.pageRequest;
  }

  public get currentPage(): PageMetadataDto {
    return this.pageMetadata;
  }

  public get currentSelectedOrder(): OrderDto | null {
    return this.selectedOrder;
  }

  @Action
  public async fetchOrder(orderId: string): Promise<StoreResponse<OrderDto>> {
    try {
      const response = await ordersApi.getUserOrderById(orderId);
      this.context.commit('setCurrentSelectedOrder', response.data);
      return StoreResponses.apiSuccess(response);
    } catch (e) {
      this.context.commit('setCurrentSelectedOrder', null);
      return StoreResponses.error(e, ErrorKey.OrderHistoryDetailFetchException);
    }
  }

  @Action
  public async fetchInitialOrders(): Promise<StoreResponse<PagedOrdersDto>> {
    try {
      const initPageRequest = cloneDeep(SearchUtility.EmptyPageRequest);
      const response = await ordersApi.getUserOrders(initPageRequest);
      this.context.commit('setPageRequest', initPageRequest);
      this.context.commit('setOrders', response.data);
      return StoreResponses.apiSuccess(response);
    } catch (e) {
      return StoreResponses.error(e, ErrorKey.OrderHistoryPageFetchException);
    }
  }

  @Action
  public async fetchAdditionalPage(pageRequest: PageRequestDto): Promise<StoreResponse<PagedOrdersDto>> {
    try {
      const response = await ordersApi.getUserOrders(pageRequest);
      this.context.commit('setPageRequest', pageRequest);
      this.context.commit('addOrders', response.data);
      return StoreResponses.apiSuccess(response);
    } catch (e) {
      return StoreResponses.error(e, ErrorKey.OrderHistoryPageFetchException);
    }
  }

  @Mutation
  private addOrders(pagedOrders: PagedOrdersDto) {
    this.orders.push(...pagedOrders.orders);
    this.pageMetadata = pagedOrders.page;
  }

  @Mutation
  private setOrders(pagedOrders: PagedOrdersDto) {
    this.orders = pagedOrders.orders;
    this.pageMetadata = pagedOrders.page;
  }

  @Mutation
  private setPageRequest(pageRequest: PageRequestDto) {
    this.pageRequest = pageRequest;
  }

  @Mutation
  private setCurrentSelectedOrder(order: OrderDto | null) {
    this.selectedOrder = order;
  }
}
